import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Stack } from "@mui/material";
import { motion } from "framer-motion";
import { useLume } from "../../hooks/useLume";
import { designSystem } from "app/theme";

/**
 * RefractivePanel - Sanctuary Edition
 * A sacred surface with refractive gold borders and 
 * hover-reactive "lume" effects. 
 */
const RefractivePanel = ({
  title,
  subtitle,
  children,
  actions,
  sx = {},
}) => {
  const containerRef = useLume();

  return (
    <Box
      ref={containerRef}
      component={motion.div}
      initial={{ opacity: 0, scale: 0.98, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "4px", // Sharp
        background: "rgba(10, 9, 8, 0.6)", // Warm Obsidian Glass
        backdropFilter: "blur(24px) saturate(110%)",
        border: "1px solid rgba(99, 102, 241, 0.2)", // Indigo subtle
        overflow: "hidden",
        p: { xs: 3, md: 5 },
        boxShadow: "0 20px 50px rgba(0, 0, 0, 0.4)",
        
        // Refractive "Light-Pipe" Border Effect (Gold)
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          padding: "1px",
          borderRadius: "inherit",
          background: `radial-gradient(
            circle at var(--lume-x, 50%) var(--lume-y, 50%), 
            rgba(99, 102, 241, 0.6) 0%, 
            transparent 50%
          )`,
          opacity: "var(--lume-opacity, 0)",
          WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          pointerEvents: "none",
          transition: "opacity 0.4s ease",
          zIndex: 10,
        },
        ...sx,
      }}
    >
      {/* Dynamic Lume Glow (Gold) */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `radial-gradient(
            circle at var(--lume-x, 50%) var(--lume-y, 50%), 
            rgba(99, 102, 241, 0.08) 0%, 
            transparent 40%
          )`,
          opacity: "var(--lume-opacity, 0)",
          transition: "opacity 0.6s ease",
          zIndex: 0,
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1 }}>
        {(title || subtitle || actions) && (
          <Box sx={{ mb: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                {title && (
                  <Typography
                    variant="h4"
                    sx={{ 
                      fontWeight: 600, 
                      letterSpacing: "0.02em", 
                      fontFamily: '"Cormorant Garamond", serif',
                      fontStyle: "italic",
                      color: "#f4f4f5" // Parchment
                    }}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#a1a1aa",
                      mt: 1,
                      fontWeight: 400,
                      fontFamily: '"Geist", sans-serif',
                      maxWidth: 600,
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {actions}
            </Stack>
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
};

RefractivePanel.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  actions: PropTypes.node,
  sx: PropTypes.object,
};

export default RefractivePanel;
